/**
 * Paginated RMA data table for the redesigned Manage Returns list: headers, rows, and pagination.
 * Sorting is controlled from the RMA details column settings popover, not from header clicks.
 */
import { observer } from "mobx-react";
import { useCallback } from "react";
import Table from "../../../../../components/table/table";
import { PAGINATION_BUTTON_COLOR } from "../../../../../components/table/table.types";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import rmaPagedListStore from "../../stores/rma-tabs-paged-list-store";
import RmaListItemStore from "../../../rma-list-page/stores/rma-item-store";
import RmaListTableRow from "../rma-list-table-row/rma-list-table-row";
import RmaListTableSettings from "../rma-list-table-settings/rma-list-table-settings";
import translationStore from "../../../../../translation/stores/translations-store";
import uiSettingsStore from "../../../../../stores/ui-settings-store";
import "./rma-list-table.scss";

const RmaListTable: React.FC = observer(() => {
    const { getTranslation } = useDashboardTranslation();
    const scope = "dashboard-returns-page";
    const rmaListFiltersStore = rmaPagedListStore.rmaListFiltersStore;
    const isLoading =
        rmaPagedListStore.isLoading ||
        rmaListFiltersStore.isLoading ||
        uiSettingsStore.isLoading ||
        uiSettingsStore.isSaving() ||
        translationStore.loadScope('resolutions').isLoading;

    const onPaginationSetPage = useCallback(async (page: number) => {
        rmaPagedListStore.resetIsLoaded();
        window.scrollTo(0, 0);
        await rmaPagedListStore.loadPage(page);
    }, []);

    return (
        <div className="rma-list-table">
            <div className="rma-list-table__inner rma-table">
                <Table
                    isLoading={isLoading}
                    totalResultsTitle="returns"
                    pagination={{
                        onSetPage: onPaginationSetPage,
                        page: rmaPagedListStore.page,
                        total: rmaPagedListStore.total,
                        perPage: rmaPagedListStore.perPage,
                        buttonColor: PAGINATION_BUTTON_COLOR.MUTED,
                    }}
                    columns={[
                        {
                            content: (
                                <span className="rma-list-table__header-with-settings">
                                    {getTranslation("RMA_DETAILS_COLUMN_HEADER", scope)}
                                    <RmaListTableSettings disabled={isLoading} />
                                </span>
                            ),
                            width: { value: 220, type: "px" },
                        },
                        { content: getTranslation("RESOLUTIONS", scope), width: { value: 1.8, type: "fr" } },
                        { content: getTranslation("ITEMS", scope), width: { value: 160, type: "px" } },
                        { content: getTranslation("CUSTOMER", scope), width: { value: 1, type: "fr" } },
                        { content: getTranslation("STATUS", scope), width: { value: 1.2, type: "fr" } },
                    ]}
                >
                    {rmaPagedListStore.list.map((rma: RmaListItemStore) => (
                        <RmaListTableRow key={rma.rmaId} rma={rma} />
                    ))}
                </Table>
            </div>
        </div>
    );
});

export default RmaListTable;
